import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  DoorOpen,
  Plus,
  Edit,
  Trash2,
  Users,
  Dumbbell,
  Settings,
  Search,
  LayoutGrid,
  List,
} from "lucide-react";
import { API } from "@/App";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useTranslation } from "react-i18next";

export default function RoomsModule({ businessId, subscription }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "packages"]);
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [rooms, setRooms] = useState([]);
  const [branches, setBranches] = useState([]);
  const [trainingTypes, setTrainingTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [deleteRoomId, setDeleteRoomId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // "grid" veya "list"
  
  // Check if user can add/edit (has active paid subscription)
  const canEdit = subscription?.is_active && subscription?.plan !== 'free';

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    capacity: "",
    branches: [],
    training_types: [],
    branch_capacities: {},  // Branch-specific capacities e.g. {"EMS": 2, "Pilates": 5}
  });
  
  // Loading state for preventing double-click
  const [isSaving, setIsSaving] = useState(false);
  
  // Capacity mode: "general" (tek kapasite) or "branch" (branşa özel)
  const [capacityMode, setCapacityMode] = useState("general");

  useEffect(() => {
    if (businessId) {
      fetchData();
    }
  }, [businessId]);

  const fetchData = async () => {
    try {
      const [roomsRes, businessRes] = await Promise.all([
        fetch(`${API}/businesses/${businessId}/rooms`, {
          credentials: "include",
        }),
        // Get business details to get branches AND training types
        fetch(`${API}/businesses/${businessId}`, {
          credentials: "include",
        }),
      ]);

      if (roomsRes.ok) setRooms(await roomsRes.json());
      
      // Get branches AND training types from business
      if (businessRes.ok) {
        const businessData = await businessRes.json();
        
        // Branches
        const businessBranches = businessData.branches || [];
        const normalizedBranches = businessBranches.map(b => {
          if (typeof b === 'string') return { id: b, name: b };
          return { id: b.id || b.name, name: b.name };
        });
        setBranches(normalizedBranches);
        
        // Training types from business - use ONLY what business has configured
        const businessTypes = businessData.training_types || [];
        const typeObjects = businessTypes.map(t => {
          if (typeof t === 'string') {
            return { id: t, label: t };
          }
          return { id: t.id || t.name, label: t.label || t.name };
        });
        setTrainingTypes(typeObjects);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error(t('rooms:roomNameRequired'));
      return;
    }
    
    // Kapasite kontrolü - seçilen moda göre
    if (capacityMode === "general") {
      if (!formData.capacity || parseInt(formData.capacity) <= 0) {
        toast.error("Genel kapasite zorunludur");
        return;
      }
    } else if (capacityMode === "branch") {
      // Branşa özel modda, seçili branşların kapasitelerini kontrol et
      const hasValidBranchCapacity = formData.branches.some(branch => {
        const cap = parseInt(formData.branch_capacities?.[branch]);
        return cap && cap > 0;
      });
      if (!hasValidBranchCapacity) {
        toast.error(t("common:toast_enAzBirBransIcin"));
        return;
      }
    }
    
    if (formData.branches.length === 0) {
      toast.error(t('common:selectAtLeastOneBranch'));
      return;
    }
    
    if (formData.training_types.length === 0) {
      toast.error(t("common:toast_enAzBirAntrenmanTipi"));
      return;
    }
    
    // Prevent double-click
    if (isSaving) return;
    setIsSaving(true);

    try {
      // Sadece geçerli training type ID'lerini kaydet (mevcut listede olmayanları filtrele)
      const validTrainingTypes = formData.training_types.filter(typeId => 
        trainingTypes.some(t => t.id === typeId)
      );
      
      // Capacity mode based logic
      let finalCapacity = parseInt(formData.capacity) || 1;
      let finalBranchCapacities = null;
      
      if (capacityMode === "branch" && formData.branches.length > 0) {
        // Branch-specific mode - use branch capacities, clear general capacity logic
        const cleanedBranchCapacities = {};
        formData.branches.forEach(branch => {
          const cap = parseInt(formData.branch_capacities[branch]);
          if (cap > 0) {
            cleanedBranchCapacities[branch] = cap;
          }
        });
        
        if (Object.keys(cleanedBranchCapacities).length > 0) {
          finalBranchCapacities = cleanedBranchCapacities;
          // Set default capacity to minimum of branch capacities (fallback)
          const caps = Object.values(cleanedBranchCapacities);
          finalCapacity = Math.min(...caps);
        }
      }
      // If capacityMode is "general", branch_capacities stays null
      
      const payload = {
        business_id: businessId,
        name: formData.name,
        capacity: finalCapacity,
        branches: formData.branches,
        training_types: validTrainingTypes,
        branch_capacities: finalBranchCapacities,
      };

      let response;
      if (editingRoom) {
        // PUT request for update
        response = await fetch(`${API}/rooms/${editingRoom.room_id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        });
      } else {
        // POST request for create
        response = await fetch(`${API}/rooms`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        });
      }

      if (response.ok) {
        toast.success(editingRoom ? t("common:roomUpdated") : t("common:roomCreated"));
        fetchData();
        handleCloseModal();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (roomId) => {
    console.log("handleDelete called with:", roomId);
    setDeleteRoomId(roomId);
  };
  
  const confirmDelete = async () => {
    if (!deleteRoomId) return;

    try {
      console.log("Sending DELETE request for room:", deleteRoomId);
      const response = await fetch(`${API}/rooms/${deleteRoomId}`, {
        method: "DELETE",
        credentials: "include",
      });
      console.log("Response status:", response.status);
      
      // Clone response to safely read body
      const responseClone = response.clone();
      let data;
      try {
        data = await responseClone.json();
        console.log("Response data:", data);
      } catch (parseErr) {
        console.error("JSON parse error:", parseErr);
        data = { detail: "Oda silinemedi" };
      }

      if (response.ok) {
        toast.success(t("common:roomDeleted"), { duration: 3000 });
        fetchData();
      } else {
        toast.error(data.detail || "Oda silinemedi", { duration: 5000 });
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(t('members:deleteFailed'), { duration: 5000 });
    } finally {
      setDeleteRoomId(null);
    }
  };

  const handleEdit = (room) => {
    setEditingRoom(room);
    
    // Kaydedilmiş training_types'ları mevcut trainingTypes listesiyle eşleştir
    // Eski ID'ler (örn: "private") veya label'lar kaydedilmiş olabilir
    const savedTypes = room.training_types || [];
    const matchedTypes = savedTypes.filter(savedType => {
      const typeStr = typeof savedType === 'object' ? savedType.id || savedType.name : savedType;
      // Mevcut training types içinde ID veya label olarak ara
      return trainingTypes.some(t => 
        t.id === typeStr || t.label === typeStr || t.name === typeStr
      );
    }).map(savedType => {
      const typeStr = typeof savedType === 'object' ? savedType.id || savedType.name : savedType;
      // Eşleşen type'ın ID'sini bul
      const found = trainingTypes.find(t => 
        t.id === typeStr || t.label === typeStr || t.name === typeStr
      );
      return found ? found.id : typeStr;
    });
    
    // Determine capacity mode based on existing data
    const hasBranchCapacities = room.branch_capacities && Object.keys(room.branch_capacities).length > 0;
    setCapacityMode(hasBranchCapacities ? "branch" : "general");
    
    setFormData({
      name: room.name || "",
      capacity: (room.capacity ?? "").toString(),
      branches: room.branches || [],
      training_types: matchedTypes,
      branch_capacities: room.branch_capacities || {},
    });
    
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRoom(null);
    setCapacityMode("general");
    setFormData({
      name: "",
      capacity: "",
      branches: [],
      training_types: [],
      branch_capacities: {},
    });
  };

  const toggleBranch = (branchName) => {
    setFormData((prev) => ({
      ...prev,
      branches: prev.branches.includes(branchName)
        ? prev.branches.filter((b) => b !== branchName)
        : [...prev.branches, branchName],
    }));
  };

  const toggleTrainingType = (typeId) => {
    setFormData((prev) => ({
      ...prev,
      training_types: prev.training_types.includes(typeId)
        ? prev.training_types.filter((t) => t !== typeId)
        : [...prev.training_types, typeId],
    }));
  };

  // Filtrelenmiş odalar
  const filteredRooms = rooms.filter((room) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      room.name?.toLowerCase().includes(query) ||
      room.branches?.some(b => b.toLowerCase().includes(query)) ||
      room.training_types?.some(t => {
        const typeStr = typeof t === 'object' ? t.name || t.id : t;
        const typeLabel = trainingTypes.find(tt => tt.id === typeStr)?.label || typeStr;
        return typeLabel.toLowerCase().includes(query);
      })
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">{t('common:rooms')}</h2>
          <p className="text-grey text-sm">{t("common:roomsDefined", { count: rooms.length })}</p>
        </div>

        <div className="flex items-center gap-2">
          {/* Görünüm Toggle */}
          <div className="flex items-center bg-slate-100 rounded-lg p-1">
            <Button
              variant="ghost"
              size="sm"
              className={`rounded-md px-2 ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={`rounded-md px-2 ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>

          {canEdit && (
            <Dialog open={showModal} onOpenChange={(open) => {
              if (!open) {
                handleCloseModal();
              }
              setShowModal(open);
            }}>
              <Button
                className="bg-navy hover:bg-navy/90 rounded-xl"
                data-testid="add-room-btn"
                onClick={() => {
                  setEditingRoom(null);
                  setCapacityMode("general");
                  setFormData({
                    name: "",
                    capacity: "",
                    branches: [],
                    training_types: [],
                    branch_capacities: {},
                  });
                  setShowModal(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t("common:newRoom")}
              </Button>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {editingRoom ? t('rooms:editRoom') : t('rooms:addNewRoom')}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>{t("common:roomName")}</Label>
                  <Input
                    placeholder={t("common:ph_ornPilatesSalonu1")}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="rounded-xl"
                    data-testid="room-name-input"
                  />
                </div>

              <div>
                <Label className="mb-2 block">{t('common:branches')}</Label>
                <div className="flex flex-wrap gap-2">
                  {branches.slice(0, 8).map((branch) => {
                    const branchName = typeof branch === 'object' ? branch.name : branch;
                    const branchKey = typeof branch === 'object' ? branch.id : branch;
                    return (
                      <button
                        key={branchKey}
                        type="button"
                        className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                          formData.branches.includes(branchName)
                            ? "bg-navy text-white"
                            : "bg-grey-100 text-grey-700 hover:bg-grey-200"
                        }`}
                        onClick={() => toggleBranch(branchName)}
                      >
                        {getLocalizedBranch(branchName)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Capacity Mode Selection */}
              <div className="border rounded-xl p-4 bg-grey-50">
                <Label className="font-medium mb-3 block">{t("common:capacitySetting")}</Label>
                
                {/* Mode Toggle */}
                <div className="flex gap-2 mb-4">
                  <button
                    type="button"
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      capacityMode === "general"
                        ? "bg-navy text-white"
                        : "bg-grey-200 text-grey-700 hover:bg-grey-300"
                    }`}
                    onClick={() => {
                      setCapacityMode("general");
                      // Clear branch capacities when switching to general
                      setFormData({ ...formData, branch_capacities: {} });
                    }}
                  >
                    {t("common:generalCapacity")}
                  </button>
                  <button
                    type="button"
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      capacityMode === "branch"
                        ? "bg-navy text-white"
                        : "bg-grey-200 text-grey-700 hover:bg-grey-300"
                    }`}
                    onClick={() => {
                      setCapacityMode("branch");
                      // Clear general capacity when switching to branch
                      setFormData({ ...formData, capacity: "", branch_capacities: formData.branch_capacities || {} });
                    }}
                    disabled={formData.branches.length === 0}
                  >
                    {t("common:branchSpecific")}
                  </button>
                </div>

                {capacityMode === "general" ? (
                  <div>
                    <p className="text-xs text-grey-500 mb-2">
                      {t("common:sameCapacityForAll")}
                    </p>
                    <Input
                      type="number"
                      min="1"
                      placeholder={t("common:ph_orn5")}
                      value={formData.capacity}
                      onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                      className="rounded-xl"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-grey-500 mb-2">
                      {t("common:differentCapacityPerBranch")}
                    </p>
                    {formData.branches.length === 0 ? (
                      <p className="text-xs text-orange-600">{t("common:onceBransSecin")}</p>
                    ) : (
                      formData.branches.map((branch) => (
                        <div key={branch} className="flex items-center gap-3">
                          <span className="text-sm w-24 truncate font-medium">{getLocalizedBranch(branch)}</span>
                          <Input
                            type="number"
                            min="1"
                            placeholder={t('rooms:capacity')}
                            value={(formData.branch_capacities || {})[branch] || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormData({
                                ...formData,
                                branch_capacities: {
                                  ...(formData.branch_capacities || {}),
                                  [branch]: val ? parseInt(val) : ""
                                }
                              });
                            }}
                            className="w-20 h-8 text-sm rounded-lg"
                          />
                          <span className="text-xs text-grey-500">{t("common:kisi")}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              <div>
                <Label className="mb-2 block">{t("common:trainingTypes")}</Label>
                <div className="flex flex-wrap gap-2">
                  {trainingTypes.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        formData.training_types.includes(type.id)
                          ? "bg-navy text-white"
                          : "bg-grey-100 text-grey-700 hover:bg-grey-200"
                      }`}
                      onClick={() => toggleTrainingType(type.id)}
                    >
                      {getLocalizedTrainingType(type.label)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl"
                  onClick={handleCloseModal}
                >
                  {t("common:cancel")}
                </Button>
                <Button
                  className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                  onClick={handleSave}
                  loading={isSaving}
                  loadingText="Kaydediliyor..."
                  data-testid="save-room-btn"
                >
                  {editingRoom ? t('settings:update') : t('settings:create')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
          )}
        </div>
        
        {!canEdit && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
            {t("packages:noActiveSubscription")}
          </div>
        )}
      </div>

      {/* Search */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grey" />
            <Input
              placeholder={t("common:ph_odaBransVeyaAntrenmanTipi")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-xl"
              data-testid="search-rooms"
            />
          </div>
        </CardContent>
      </Card>

      {/* Rooms Grid/List */}
      {filteredRooms.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredRooms.map((room) => (
              <Card
                key={room.room_id}
                className="border-0 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`room-card-${room.room_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-navy/10 rounded-xl flex items-center justify-center">
                        <DoorOpen className="w-6 h-6 text-navy" />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-lg text-navy">
                          {room.name}
                        </h3>
                        <div className="flex items-center gap-1 text-sm text-grey">
                          <Users className="w-3 h-3" />
                          {room.branch_capacities && Object.keys(room.branch_capacities).length > 0 ? (
                            <span className="text-xs">{t("common:bransaOzelKapasite")}</span>
                          ) : (
                            <span>{room.capacity} {t("common:personCount2")}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => handleEdit(room)}
                      >
                        <Edit className="w-4 h-4 text-grey" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => handleDelete(room.room_id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>

                  {/* Branch Capacities - show if using branch-specific */}
                  {room.branch_capacities && Object.keys(room.branch_capacities).length > 0 && (
                    <div className="mb-3 p-2 bg-blue-50 rounded-lg">
                      <p className="text-xs text-grey mb-1.5 font-medium">{t("common:bransKapasiteleri")}</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(room.branch_capacities).map(([branch, cap]) => (
                          <span key={branch} className="text-xs bg-white px-2 py-1 rounded-md">
                            {getLocalizedBranch(branch)}: <strong>{cap}</strong> {t("common:personCount2")}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Branches */}
                  {room.branches?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-grey mb-1.5">{t('common:branches')}</p>
                      <div className="flex flex-wrap gap-1">
                        {room.branches.map((branch, idx) => {
                          const branchName = typeof branch === 'object' ? branch.name : branch;
                          return (
                            <Badge key={`${branchName}-${idx}`} variant="secondary" className="text-xs">
                              {getLocalizedBranch(branchName)}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Training Types */}
                  {room.training_types?.length > 0 && (
                    <div>
                      <p className="text-xs text-grey mb-1.5">{t("common:trainingTypes")}</p>
                      <div className="flex flex-wrap gap-1">
                        {room.training_types.map((type, idx) => {
                          const typeStr = typeof type === 'object' ? type.name || type.id : type;
                          const typeLabel = trainingTypes.find((t) => t.id === typeStr)?.label || typeStr;
                          return (
                            <Badge key={`${typeStr}-${idx}`} variant="outline" className="text-xs">
                              {getLocalizedTrainingType(typeLabel)}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          /* List View */
          <Card className="border-0 shadow-sm">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">{t('common:room')}</TableHead>
                    <TableHead className="whitespace-nowrap">{t('rooms:capacity')}</TableHead>
                    <TableHead className="whitespace-nowrap">{t('common:branches')}</TableHead>
                    <TableHead className="whitespace-nowrap">{t("common:trainingTypes")}</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRooms.map((room) => (
                    <TableRow key={room.room_id} data-testid={`room-row-${room.room_id}`}>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <DoorOpen className="w-5 h-5 text-navy" />
                          </div>
                          <span className="font-medium">{room.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center gap-1 text-sm">
                          <Users className="w-3 h-3 text-grey" />
                          {room.branch_capacities && Object.keys(room.branch_capacities).length > 0 ? (
                            <div className="flex flex-col">
                              {Object.entries(room.branch_capacities).slice(0, 2).map(([branch, cap]) => (
                                <span key={branch} className="text-xs">{branch}: {cap}</span>
                              ))}
                              {Object.keys(room.branch_capacities).length > 2 && (
                                <span className="text-xs text-grey">+{Object.keys(room.branch_capacities).length - 2} daha</span>
                              )}
                            </div>
                          ) : (
                            <span>{room.capacity} {t("common:personCount2")}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {room.branches?.slice(0, 2).map((branch, idx) => {
                            const branchName = typeof branch === 'object' ? branch.name : branch;
                            return (
                              <Badge key={`${branchName}-${idx}`} variant="secondary" className="text-xs">
                                {getLocalizedBranch(branchName)}
                              </Badge>
                            );
                          })}
                          {room.branches?.length > 2 && (
                            <Badge variant="secondary" className="text-xs">+{room.branches.length - 2}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {room.training_types?.slice(0, 2).map((type, idx) => {
                            const typeStr = typeof type === 'object' ? type.name || type.id : type;
                            const typeLabel = trainingTypes.find((t) => t.id === typeStr)?.label || typeStr;
                            return (
                              <Badge key={`${typeStr}-${idx}`} variant="outline" className="text-xs">
                                {getLocalizedTrainingType(typeLabel)}
                              </Badge>
                            );
                          })}
                          {room.training_types?.length > 2 && (
                            <Badge variant="outline" className="text-xs">+{room.training_types.length - 2}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => handleEdit(room)}
                          >
                            <Edit className="w-4 h-4 text-grey" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => handleDelete(room.room_id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <DoorOpen className="w-12 h-12 text-grey mx-auto mb-3" />
            <p className="text-grey">
              {searchQuery ? t("common:roomNotFound") : t("rooms:noRoomsAdded")}
            </p>
            {!searchQuery && (
              <p className="text-sm text-grey mt-1">
                {t("common:defineRoomsForReservation")}
              </p>
            )}
            {canEdit && !searchQuery && (
              <Button
                className="mt-4 bg-navy hover:bg-navy/90 rounded-xl"
                onClick={() => setShowModal(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t("common:addFirstRoom")}
              </Button>
            )}
            {!canEdit && !searchQuery && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                {t("admin:subscriptionRequiredForRoom")}
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteRoomId} onOpenChange={(open) => !open && setDeleteRoomId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("common:odayiSil")}</DialogTitle>
            <DialogDescription>
              {t("common:deleteRoomConfirm")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteRoomId(null)}
              className="rounded-xl"
            >
              {t("common:cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              className="rounded-xl"
            >
              Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
